import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { isMobile, isTablet } from 'react-device-detect';
import { BarcodeScanner, ScanButton } from '../../Common';
import { isBarcodeScanSupported } from '../../../utils';
import { TranslateW } from '../../../Utils/Languages/Translate';
import { chromeInfoBackground } from '../../../color_utils';

const SEARCH_FIELDS = ['tagNumber', 'serialNumber'];

// Prefer an exact field match (a real, full barcode resolves to one appliance), then fall back to a
// case-insensitive Contains — same behavior as typing in the dropdown and the Inventory scan lookup.
const matchAssets = (assets, value) => {
    const v = value.trim().toLowerCase();
    if (!v) return [];
    const exact = assets.filter(a =>
        SEARCH_FIELDS.some(f => a[f] && String(a[f]).trim().toLowerCase() === v)
    );
    if (exact.length > 0) return exact;
    return assets.filter(a =>
        SEARCH_FIELDS.some(f => a[f] && String(a[f]).toLowerCase().includes(v))
    );
};

const ApplianceScanLookup = ({ assets, onSelect, onSearchValue, serviceEventType, disabled }) => {
    const [open, setOpen] = useState(false);
    const scanLabel = TranslateW('ScanBarcode');
    const scanTitle = TranslateW('FexaTrakref');

    if (!isBarcodeScanSupported() || !(isMobile || isTablet)) return null;

    const close = () => {
        setOpen(false);
    };

    const select = (assetId) => {
        onSelect(assetId);
        close();
    };

    const handleDecode = (value) => {
        const found = matchAssets(assets, value);
        if (found.length === 1) {
            select(found[0].id);
            return;
        }
        close();
        onSearchValue(value);
    };

    return (
        <>
            <ScanButton
                onClick={() => setOpen(true)}
                disabled={disabled}
                tooltipText={scanLabel}
                ariaLabel={scanLabel}
                size="small"
                iconSize={18}
                badgeSx={{ '& .MuiBadge-badge': { fontSize: '8px', height: 14, minWidth: 26, top: 0, right: 0 } }}
                sx={{
                    backgroundColor: chromeInfoBackground,
                    borderRadius: '6px',
                    '&:hover': { backgroundColor: chromeInfoBackground },
                }}
            />
            <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(0,0,0,0.23)', mx: 0.5 }} />
            {open && (
                <BarcodeScanner
                    open={open}
                    onClose={close}
                    onDecode={handleDecode}
                    title={scanTitle}
                    source="service-event-scan"
                    sourceContext={serviceEventType}
                />
            )}
        </>
    );
};

ApplianceScanLookup.propTypes = {
    assets: PropTypes.array,
    onSelect: PropTypes.func.isRequired,
    onSearchValue: PropTypes.func.isRequired,
    serviceEventType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
};

ApplianceScanLookup.defaultProps = {
    assets: [],
    serviceEventType: undefined,
    disabled: false,
};

export default ApplianceScanLookup;
